import {
  useEffect, useMemo, useReducer, useRef, useState,
} from 'react';
import {
  Alert, Button, Chips, Icon, IconButton, Modal, Typography,
} from '@gupshup-frontend/gupshup-ts-components';
import TemplatePreview from '../../../components/TemplatePreview';
import { createTemplate, templateErrorMessage, updateTemplate } from '../../../api/templates';
import {
  formReducer, HEADER_TYPE, BUTTON_TYPE, URL_TYPE, isCarousel, isLto, isCallPermissionRequest,
  isAuthentication, isPasskey, initialFormState, COPY_CODE_BUTTON_LABEL, URL_TYPE_LINK_TRACK_LABEL,
  quickRepliesFirst,
} from './state';
import {
  buildPayload, bucketButtons, buildCarouselCard, effectiveFooter,
} from './payload';
import { validateForm, clampExpirationTime } from './validate';
import { prefillFormState, detectDroppedFeatures } from './prefill';
import { SECTIONS } from './sections';

const EMPTY_ERRORS = {};

// Upstream status literal (helper/templateStatus.js's TEMPLATE_STATUS.ENABLED key) — the console
// only warns on this one status, not on every non-terminal one.
const ENABLED_STATUS = 'ENABLED';

// Replaces every {{n}} token in `text` with its sample value from `variables`
// ([{name: '{{n}}', value, error}], see sections/Variables.js) so the live preview shows
// realistic content instead of raw tokens. Falls back to the token itself while a sample value
// hasn't been typed yet. split/join instead of a regex — token text like "{{1}}" contains regex
// metacharacters, and split/join treats it as a literal substring with no escaping needed.
function substituteVariables(text, variables) {
  if (!text) return text;
  return (variables || []).reduce(
    (acc, variable) => (variable?.name ? acc.split(variable.name).join(variable.value || variable.name) : acc),
    text,
  );
}

// Authentication (Copy Code/Autofill/Zero-Tap) templates get a fixed, WhatsApp-supplied body and
// an optional footer that this form has no field for — sections/Body.js and sections/Footer.js
// both render nothing for isAuthentication(state) && !isPasskey(state), so state.body.value and
// state.footer.footerText.value stay '' forever for this case. Console-mirrored text instead
// (gs-ui-templates C/Body/index.tsx's AUTH_BODY + sample "123456", C/Auth/MessageContent.tsx's
// security-disclaimer append and expiry-footer text) — null for Passkey (which renders its own
// real body/footer fields) and for every non-auth category.
function authOtpDisplay(state) {
  if (!isAuthentication(state) || isPasskey(state)) return null;
  const { isSecurityCheck, isExpirationTimeChecked, expirationTime } = state.messageContent;
  const template = isSecurityCheck
    ? '{{1}} is your verification code. For your security, do not share this code.'
    : '{{1}} is your verification code.';
  const minutes = clampExpirationTime(expirationTime.value);
  const footer = (isExpirationTimeChecked && /^\d+$/.test(minutes || ''))
    ? `This code expires in ${minutes === '1' ? '1 minute' : `${minutes} minutes`}`
    : '';
  return { template, sample: template.replace('{{1}}', '123456'), footer };
}

// Builds the `template` prop components/TemplatePreview expects — see its own index.js:
// `{ data, category, templateType, containerMeta }`, where containerMeta needs at least
// `data`/`header`/`footer`/`mediaUrl`/`buttons` (WhatsAppPreview.js). helper/utils.js's
// mapTemplate builds this same shape from an already-created Enterprise template by spreading
// its raw fields into containerMeta; this is the equivalent built from live form state instead.
//
// Buttons/LTO reuse payload.js's own wire builders (bucketButtons/buildCarouselCard) so the
// preview can never disagree with what the real submit payload sends. Carousel cards are the one
// exception: buildCarouselCard's `body`/`header` keys are the CarouselCardDto wire shape
// (`{text, example}` / `{id}`), not what CarouselCardPanel.js/Header.js/Content.js actually read
// (`card.body` as a plain string, `card.mediaUrl`/`card.headerType`) — reusing them as-is hands
// Content.js an object where it expects a string, which throws ("content.replace is not a
// function"). Only buildCarouselCard's `buttons` is genuinely preview-shaped, so only that piece
// is reused below; the rest is built from the raw card state. `objectUrl` is this card's own live
// blob preview (TemplateForm's cardMediaPreviewUrls, keyed by cardId) — Carousel.js always writes
// cardMedia.mediaSrc as '' for a freshly (re-)uploaded file (only `file`/`mediaId` are populated
// there), so mediaSrc alone is only ever real for an edit/clone's already-hosted media. Never add
// card.cardMedia.mediaId here — TemplatePreview/index.js:56-58 + WhatsAppPreview.js's own
// card-media fixup key off array POSITION among cards that have a mediaId but no mediaUrl, not
// cardId, so one such card ahead of this one would blank THIS card's mediaUrl instead of its own.
function buildPreviewCard(card, mediaType, objectUrl) {
  const { buttons } = buildCarouselCard(card);
  return {
    mediaUrl: objectUrl || card.cardMedia.mediaSrc || '',
    headerType: mediaType,
    body: card.description.value,
    sampleText: substituteVariables(card.description.value, card.variable),
    buttons,
  };
}

function buildPreviewTemplate(state, mediaPreviewUrl, cardMediaPreviewUrls) {
  const isTextHeader = state.header.value === HEADER_TYPE.TEXT;
  const isMediaHeader = state.header.value === HEADER_TYPE.MEDIA;
  const carousel = isCarousel(state);
  const auth = authOtpDisplay(state);

  const bodyText = auth ? auth.sample : substituteVariables(state.body.value, state.body.variable);
  const headerText = isTextHeader
    ? substituteVariables(state.header.headerText.value, state.header.headerText.variable)
    : '';
  // LOCATION (state contract: headerMedia.value = 'LOCATION', never a real upload — state.js) needs
  // no special-casing here: mediaPreviewUrl only ever gets set from a picked `file` (the effect
  // below), which a LOCATION header never has, and headerMedia.mediaSrc is never populated for it
  // either (prefill.js) — so mediaUrl already comes out '' for it, same as any other never-uploaded
  // media header. TemplatePreview/Header.js's own headerType === TEMPLATE_TYPE.LOCATION branch
  // renders its static pin icon unconditionally and short-circuits on an empty mediaUrl before any
  // fetch/validateMediaUrl call — verified there, not guessed.
  const mediaUrl = isMediaHeader ? (mediaPreviewUrl || state.header.headerMedia.mediaSrc || '') : '';
  // Carousel and media-header are mutually exclusive by category/subcategory already (a carousel
  // template never reaches the MEDIA header branch), so the check order between them doesn't
  // matter for correctness — carousel is checked first simply because it's the new special case.
  // LOCATION passes straight through as state.header.headerMedia.value ('LOCATION'), which is
  // exactly TemplatePreview's own TEMPLATE_TYPE.LOCATION literal — no separate branch needed.
  const templateType = carousel
    ? 'CAROUSEL'
    : (isMediaHeader ? (state.header.headerMedia.value || 'TEXT') : 'TEXT');

  const { cta, quickReply, otp, passkey } = bucketButtons(state);
  const quickReplyButtons = quickReply.map((text) => ({ type: BUTTON_TYPE.QUICK_REPLY, text }));
  // Same group-order rule sections/Buttons.js renders by and payload.js's computeQuickReplyFirst
  // sends on the wire (state.js's quickRepliesFirst — see its own comment) — so the live/preview
  // WhatsApp bubble (and, via containerMeta.buttons below, the Preview & Submit modal's
  // details-card button line) can never show an order the form itself didn't. The Marketing
  // opt-out reply is already the LAST entry in `quickReply` (bucketButtons appends it), so it
  // stays last within its own group regardless of which group renders first here.
  const buttons = quickRepliesFirst(state)
    ? [...quickReplyButtons, ...cta]
    : [...cta, ...quickReplyButtons];
  if (otp) buttons.push(otp);
  if (passkey) buttons.push(passkey);

  return {
    data: bodyText,
    category: state.category.value,
    templateType,
    languageCode: state.language.value,
    containerMeta: {
      data: bodyText,
      header: headerText,
      mediaUrl,
      // payload.js's effectiveFooter — exactly what the real submit payload sends as footer,
      // including its carousel/opt-out gating — so the preview can never disagree with it.
      // authOtpDisplay's own footer (console-mirrored expiry text) takes over for auth non-Passkey,
      // since effectiveFooter(state) is always '' there (state.footer never renders a field for it).
      footer: auth ? auth.footer : (effectiveFooter(state) || ''),
      buttons,
      cards: carousel
        ? state.carousels.cards.map((card) => buildPreviewCard(
          card, state.carousels.mediaType, cardMediaPreviewUrls[card.cardId],
        ))
        : [],
      // Omitted entirely (not set to null/undefined) when there's no LTO — WhatsAppPreview.js
      // gates its <LimitedTimeOffer> render on `containerMeta?.limitedTimeOffer &&`, so an absent
      // key and an explicit falsy value behave identically there; omitting matches how this
      // object never carried the key at all before LTO existed.
      ...(isLto(state) && {
        limitedTimeOffer: { text: state.lto.offerTitle.value, has_expiration: state.lto.isExpiryChecked },
      }),
      // WhatsAppPreview.js reads this to swap in the Call Permission Request panel and its own
      // fixed button (components/TemplatePreview/WhatsAppPreview.js:136,277; ButtonPanel.js:152).
      ...(isCallPermissionRequest(state) && { isCPR: true }),
    },
  };
}

// Create/edit/clone template form shell. Mounted by ../index.js for the 'create', 'edit' and
// 'clone' intents — see the useEffect below for where an existing template's data gets prefilled
// into this form's state on an edit OR clone open. `template` and `cloneSource` are mutually
// exclusive (../index.js only ever sets one per pendingAction): `template` drives both the
// prefill and isEdit below, `cloneSource` drives only the prefill, so a clone starts from the
// same data an edit would but still submits as a brand new template.
const TemplateForm = ({
  orgId, channelId, channelLabel, isMmLite, template, cloneSource, onClose, onSaved, onError,
}) => {
  const [state, dispatch] = useReducer(formReducer, undefined, initialFormState);
  const [errors, setErrors] = useState(EMPTY_ERRORS);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [mediaPreviewUrl, setMediaPreviewUrl] = useState('');
  // Scroll target for the submit-error Alert below — a long details card (many buttons, a long
  // example) can still push the modal's content tall enough for a fresh error to render off-screen.
  const submitErrorRef = useRef(null);

  const isEdit = Boolean(template);
  const isClone = Boolean(cloneSource);

  // Translates an existing Enterprise template row into this form's state shape on an edit OR
  // clone open, so the form starts from the real template instead of blank. formReducer's 'reset'
  // action (state.js) does a wholesale state replace with action.state, which is exactly what
  // prefillFormState's return value is meant to become.
  useEffect(() => {
    const source = template || cloneSource;
    if (!source) return;
    const prefilled = prefillFormState(source);
    // A clone must not reuse the original's name — the console's own clone leaves it blank so
    // the user has to type a new, unique one; everything else stays prefilled and editable.
    if (cloneSource) prefilled.templateName = { value: '', error: '' };
    dispatch({ type: 'reset', state: prefilled });
  }, [template, cloneSource]);

  // `type` (payload.js's top-level wire field) is a value DERIVED from the header selection —
  // Header.js keeps it in sync on every header change, and Category.js resets it alongside
  // header on every category/subcategory change. But initialFormState() itself leaves `type`
  // blank, and nothing re-syncs it until the user actually touches Header or Category — so a
  // plain text-body template submitted without ever opening either control would send
  // `type: ''` (payload.js's omitEmpty only strips undefined/null, not ''). Seed it once on
  // mount to match the NONE-header default so the plain happy path works untouched.
  // isEdit/isClone guard: on an edit OR clone open, the 'reset' effect above already seeded the
  // real type from prefillFormState(template || cloneSource). Both effects fire on mount in
  // declaration order, so without this guard this effect would unconditionally stomp that real
  // type back to 'TEXT' right after — silently corrupting the type of any cloned
  // IMAGE/VIDEO/DOCUMENT/GIF/CAROUSEL template the moment its form opens, even if the user never
  // touches Header again before submitting (the live preview wouldn't show it either, since
  // buildPreviewTemplate below derives its own templateType from the header state, not from
  // this field — only the actual submit payload would be wrong).
  useEffect(() => {
    if (isEdit || isClone) return;
    dispatch({ type: 'set', path: 'type.value', value: 'TEXT' });
  }, [isEdit, isClone]);

  const pickedFile = state.header.headerMedia.file;
  useEffect(() => {
    if (!pickedFile) {
      setMediaPreviewUrl('');
      return undefined;
    }
    const url = URL.createObjectURL(pickedFile);
    setMediaPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [pickedFile]);

  // Per-card object URL for a freshly picked carousel media file — same createObjectURL/
  // revokeObjectURL lifecycle as mediaPreviewUrl above, keyed by cardId so N cards can each hold
  // their own pending upload without stepping on a neighbour's blob: url. cardMediaRef remembers
  // which File each url was minted for, so a run only revokes+recreates the cards that actually
  // changed instead of thrashing every card on any unrelated carousel keystroke — state.carousels.
  // cards gets a new array reference on every dispatch, so that reference alone can't be the signal.
  const [cardMediaPreviewUrls, setCardMediaPreviewUrls] = useState({});
  const cardMediaRef = useRef({});
  useEffect(() => {
    const prev = cardMediaRef.current;
    const currentIds = new Set(state.carousels.cards.map((card) => card.cardId));
    const next = {};
    let changed = false;

    state.carousels.cards.forEach((card) => {
      const { file } = card.cardMedia;
      const existing = prev[card.cardId];
      if (file) {
        if (existing && existing.file === file) {
          next[card.cardId] = existing;
        } else {
          if (existing) URL.revokeObjectURL(existing.url);
          next[card.cardId] = { file, url: URL.createObjectURL(file) };
          changed = true;
        }
      } else if (existing) {
        URL.revokeObjectURL(existing.url);
        changed = true;
      }
    });
    // A card removed outright (not just cleared) never reaches the loop above — revoke whatever
    // it was still holding.
    Object.keys(prev).forEach((cardId) => {
      if (!currentIds.has(cardId)) {
        URL.revokeObjectURL(prev[cardId].url);
        changed = true;
      }
    });

    cardMediaRef.current = next;
    if (changed) {
      const urls = {};
      Object.keys(next).forEach((cardId) => { urls[cardId] = next[cardId].url; });
      setCardMediaPreviewUrls(urls);
    }
  }, [state.carousels.cards]);

  // Revoke whatever's left on unmount — the effect above already revokes on every in-session
  // change (file replaced/cleared/card removed); this only covers the form closing with one
  // still outstanding.
  useEffect(() => () => {
    Object.values(cardMediaRef.current).forEach(({ url }) => URL.revokeObjectURL(url));
  }, []);

  // A fresh submit error can render below the fold inside the tall preview modal — bring it
  // into view instead of leaving the user staring at an unchanged screen.
  useEffect(() => {
    if (submitError) submitErrorRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [submitError]);

  const previewTemplate = useMemo(
    () => buildPreviewTemplate(state, mediaPreviewUrl, cardMediaPreviewUrls),
    [state, mediaPreviewUrl, cardMediaPreviewUrls],
  );

  // Computed only while the modal is open — buildPayload does real derivation work (category/
  // type, header, button bucketing, example arrays) the always-mounted live preview above doesn't
  // need paying for. Guarded: a throw here must never crash the modal, only hide the payload-
  // derived lines below — handleConfirm's own buildPayload call (and its catch) is what actually
  // matters for a real submit failure.
  const previewPayload = useMemo(() => {
    if (!showPreviewModal) return null;
    try {
      return buildPayload(state, { isEdit });
    } catch {
      return null;
    }
  }, [showPreviewModal, state, isEdit]);

  const handleSubmitClick = () => {
    const nextErrors = validateForm(state, { isEdit });
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }
    setErrors(EMPTY_ERRORS);
    setSubmitError('');
    setShowPreviewModal(true);
  };

  const handleConfirm = async () => {
    setSubmitting(true);
    setSubmitError('');
    const file = state.header.headerMedia.file || undefined;
    try {
      const payload = buildPayload(state, { isEdit });
      if (isEdit) {
        await updateTemplate(orgId, channelId, String(template.id), payload, file);
      } else {
        await createTemplate(orgId, channelId, payload, file);
      }
      setShowPreviewModal(false);
      onSaved();
      onClose();
    } catch (err) {
      // Never close on failure — keep the modal open with the error inline so the user can retry.
      const message = templateErrorMessage(err);
      setSubmitting(false);
      setSubmitError(message);
      // The inline alert sits inside a tall, scrollable modal and can be off-screen; the page-level
      // toast (Templates/index.js) guarantees the failure is visible.
      onError?.(message);
      return;
    }
    setSubmitting(false);
  };

  // Routes through the reducer's own 'reset' action (the same mechanism the edit-open effect
  // above uses) instead of hand-rebuilding state, so "cleared" can never drift from what that
  // effect already treats as blank. The sibling useState flags below track state that belongs to
  // this same editing session and would otherwise survive a reducer reset untouched: errors and
  // submitError are leftovers from a previous validate/submit attempt against the form we're now
  // discarding, and showPreviewModal is reset too so a stale preview can never outlive the form it
  // was built from. mediaPreviewUrl isn't listed here — the pickedFile effect above already reacts
  // to state.header.headerMedia.file going back to null and revokes/clears it on its own.
  const handleClearAllConfirm = () => {
    dispatch({ type: 'reset', state: initialFormState() });
    setErrors(EMPTY_ERRORS);
    setSubmitError('');
    setShowPreviewModal(false);
    setShowClearConfirm(false);
  };

  const showEnabledWarning = isEdit && template?.status === ENABLED_STATUS;

  // What prefillFormState silently couldn't carry over from the source template (see prefill.js's
  // own detectDroppedFeatures) — computed once per source, not per keystroke. `template` and
  // `cloneSource` are mutually exclusive (see this component's own header comment), so this is
  // always evaluated against whichever one actually drove the prefill effect above. Shown to the
  // user on both Edit and Clone (see the two Alerts below) — Edit didn't used to compute this at
  // all, which hid the exact same silent data loss on the more destructive of the two paths.
  const droppedFeatures = useMemo(
    () => detectDroppedFeatures(template || cloneSource),
    [template, cloneSource],
  );

  // Console parity: three mutually exclusive page titles depending on how this form was opened.
  let formTitle = 'Create Template';
  if (isEdit) formTitle = 'Edit Template';
  else if (isClone) formTitle = 'Clone Template';

  // Authentication non-Passkey has no state-driven body/footer (see authOtpDisplay's own
  // comment) — the Template/Example lines below fall back to its fixed, sample-filled text
  // instead of the (always blank) payload-derived ones for that case.
  const auth = authOtpDisplay(state);

  // previewTemplate's buttons already carry a resolved display label in `.text` for both cases
  // the console's PreviewSubmit.tsx calls out — the Marketing opt-out quick reply (bucketButtons
  // appends MARKETING_OPT_OUT_BUTTON_TEXT as its `text`) and an Authentication OTP_TYPE.COPY_CODE
  // button (state.js seeds btn.text with OTP_COPY_CODE_BUTTON_TEXT, user-editable after). The one
  // gap: a standalone MARKETING/UTILITY COPY_CODE CTA button carries no `text` anywhere in this
  // pipeline (payload.js's CTA_BUILDERS omits it on purpose, matching the console's own locked
  // label) — fall back to that same fixed label so the line never renders an empty "[]".
  const buttonLine = (previewTemplate.containerMeta.buttons || [])
    .map((btn) => {
      const text = btn.text || (btn.type === BUTTON_TYPE.COPY_CODE ? COPY_CODE_BUTTON_LABEL : '');
      // Link-tracked buttons carry no url on the wire (payload.js sends neither url nor example;
      // Enterprise fills it from the account's link-tracking domain) — show the type instead of
      // an empty suffix. A DYNAMIC url shows `url{{1}}`, console-matched (createTemplateHooks.ts's
      // buttonsData) — display only, payload.js still sends the bare base (CTA_BUILDERS[URL]).
      const urlSuffix = btn.type === BUTTON_TYPE.URL
        ? `, ${btn.urlType === URL_TYPE.DYNAMIC_LINK_TRACK
          ? URL_TYPE_LINK_TRACK_LABEL
          : `${btn.url || ''}${btn.urlType === URL_TYPE.DYNAMIC ? '{{1}}' : ''}`}`
        : '';
      return `[${text}${urlSuffix}]`;
    })
    .join(' | ');

  // Example block: the header sample (its one {{1}} replaced) plus the body with every {{n}}
  // replaced by its own sample — same substituteVariables helper the live preview above already
  // uses, just fed the payload's own headerExamples/templateVariableExamples instead of state's
  // variable rows, since those arrays are what actually ships to WhatsApp as the example.
  const headerVariables = previewPayload?.headerExamples?.length
    ? [{ name: '{{1}}', value: previewPayload.headerExamples[0] }]
    : [];
  const bodyVariables = (previewPayload?.templateVariableExamples || [])
    .map((value, index) => ({ name: `{{${index + 1}}}`, value }));
  const headerExample = previewPayload?.header ? substituteVariables(previewPayload.header, headerVariables) : '';
  const templateText = auth ? auth.template : (previewPayload?.template || '');
  const bodyExample = auth
    ? auth.sample
    : (previewPayload?.template ? substituteVariables(previewPayload.template, bodyVariables) : '');
  const exampleText = [headerExample, bodyExample].filter(Boolean).join('\n');

  return (
    <div className="br-tpl-form" data-testid="bloomreach-templates-form-root">
      <div className="br-tpl-form-header">
        <IconButton
          size="small"
          disabled={submitting}
          onClick={onClose}
          data-testid="bloomreach-templates-form-back"
        >
          <Icon name="caretLeft" size={20} showCursor />
        </IconButton>
        <Typography variant="h6" data-testid="bloomreach-templates-form-title">
          {formTitle}
        </Typography>
        {/* Console parity (gs-ui-templates C/index.tsx's AppNameDisplay) — the channel this
            template is being created/edited/cloned against, next to the title. */}
        {channelLabel && (
          <Chips
            data={[{
              label: channelLabel,
              size: 'small',
              color: 'default',
              'data-testid': 'bloomreach-templates-form-channel-chip',
            }]}
          />
        )}
      </div>

      {showEnabledWarning && (
        <Alert
          type="warning"
          description="Any scheduled or in-progress campaigns with this template may fail when you edit this approved template"
          data-testid="bloomreach-templates-form-enabled-warning"
        />
      )}

      {/* Edit is the destructive path — saving here overwrites the live template in place, so
          anything prefill couldn't represent is gone for good the moment the user hits Submit.
          Clone only risks a redundant new template, so it keeps its own, softer wording below. */}
      {isEdit && droppedFeatures.length > 0 && (
        <Alert
          type="warning"
          description={`Some parts of this template can't be edited here and will be removed if you save: ${droppedFeatures.join(' ')}`}
          data-testid="bloomreach-templates-form-edit-dropped-warning"
        />
      )}

      {isClone && droppedFeatures.length > 0 && (
        <Alert
          type="warning"
          description={`This clone couldn't carry over everything from the original template. ${droppedFeatures.join(' ')}`}
          data-testid="bloomreach-templates-form-clone-dropped-warning"
        />
      )}

      <div className="br-tpl-form-panes">
        <div className="br-tpl-form-pane-form">
          {SECTIONS.map(({ key, Component, extraProps }) => (
            <Component
              key={key}
              state={state}
              dispatch={dispatch}
              errors={errors}
              disabled={submitting}
              isEdit={isEdit}
              // Sections needing props beyond the common set declare their own extraProps(ctx) —
              // see sections/index.js. Every section without one gets no extra props, unchanged.
              {...(extraProps ? extraProps({ orgId, channelId, isMmLite }) : null)}
            />
          ))}

          <div className="br-tpl-form-actions">
            {/* Console-parity action (EditTemplateButtons.tsx's SubmitToChecker branch), CREATE
                flow only — editing an existing template has nothing to "clear back to". */}
            {!isEdit && (
              <Button
                variant="outlined"
                disabled={submitting}
                onClick={() => setShowClearConfirm(true)}
                data-testid="bloomreach-templates-form-clear-all"
              >
                Clear all
              </Button>
            )}
            <Button
              variant="contained"
              disabled={submitting}
              onClick={handleSubmitClick}
              data-testid="bloomreach-templates-form-submit"
            >
              Preview and Submit
            </Button>
          </div>
        </div>

        <div className="br-tpl-form-pane-preview">
          <TemplatePreview
            width="100%"
            height="650px"
            template={previewTemplate}
            scrollOnhover
            isInPreview
          />
        </div>
      </div>

      <Modal
        data={{
          title: 'Preview',
          content: (
            <div className="br-tpl-form-preview-modal" data-testid="bloomreach-templates-form-preview-modal-body">
              {submitError && (
                <div ref={submitErrorRef}>
                  <Alert
                    type="error"
                    title="Template not submitted"
                    description={submitError}
                    data-testid="bloomreach-templates-form-submit-error"
                  />
                </div>
              )}
              {/* Console-mirrored layout (PreviewSubmit.tsx): details card left, phone preview
                  right, instead of a full-width bubble under a 2-column summary grid. */}
              <div className="br-tpl-form-preview-row">
                <div className="br-tpl-form-preview-col">
                  <Typography variant="body2" className="br-tpl-form-preview-subtitle">
                    Preview your template before it is submitted to WhatsApp
                  </Typography>
                  <div className="br-tpl-form-preview-card" data-testid="bloomreach-templates-form-summary">
                    <p className="br-tpl-form-preview-title">{state.templateName.value}</p>
                    {previewPayload && (
                      <>
                        <p>{`Category: ${previewPayload.category}`}</p>
                        <p>{`Language: ${state.language.value}, Type: ${previewPayload.type}`}</p>
                        {previewPayload.header && (
                          <p className="br-tpl-form-preview-multiline">{`Header:\n${previewPayload.header}`}</p>
                        )}
                        {/* Auth copy-code/autofill/zero-tap templates have no state-driven body —
                            WhatsApp supplies it server-side, so previewPayload.template stays ''
                            here; templateText falls back to authOtpDisplay's own text for that
                            case (see its own comment above). */}
                        {(templateText || buttonLine) && (
                          <p className="br-tpl-form-preview-multiline">
                            {['Template:', templateText, buttonLine].filter(Boolean).join('\n')}
                          </p>
                        )}
                        {/* Console always renders this line (PreviewSubmit.tsx's renderLabel is
                            called unconditionally for "example_text") and appends the same button
                            line under it too — match both instead of gating on samples existing. */}
                        <p className="br-tpl-form-preview-multiline">
                          {['Example:', exampleText, buttonLine].filter(Boolean).join('\n')}
                        </p>
                      </>
                    )}
                  </div>
                </div>
                <TemplatePreview width="400px" height="450px" template={previewTemplate} />
              </div>
            </div>
          ),
          actionButtonProps: [
            {
              children: 'Cancel',
              variant: 'outlined',
              disabled: submitting,
              onClick: () => setShowPreviewModal(false),
              'data-testid': 'bloomreach-templates-form-preview-cancel',
            },
            {
              children: 'Submit',
              variant: 'contained',
              loading: submitting,
              disabled: submitting,
              onClick: handleConfirm,
              'data-testid': 'bloomreach-templates-form-preview-submit',
            },
          ],
        }}
        config={{
          open: showPreviewModal,
          onClose: () => {
            if (!submitting) setShowPreviewModal(false);
          },
          showCloseIcon: true,
          closeIconTestId: 'bloomreach-templates-form-preview-close',
          maxWidth: 'lg',
          actionButtonPlacement: 'center',
          'data-testid': 'bloomreach-templates-form-preview-modal',
        }}
      />

      {/* Same Modal pattern as the preview dialog above / DeleteModal.js — a destructive action
          needs a confirmation step, not a native window.confirm blocking the page. CREATE-only,
          same gate as the trigger button, so this can never mount for an edit. */}
      {!isEdit && (
        <Modal
          data={{
            title: 'Discard all changes?',
            content: (
              <>
                <p>This clears every field on this form.</p>
                <p>This cannot be undone.</p>
              </>
            ),
            actionButtonProps: [
              {
                children: 'Cancel',
                variant: 'outlined',
                onClick: () => setShowClearConfirm(false),
                'data-testid': 'bloomreach-templates-form-clear-all-cancel',
              },
              {
                children: 'Clear all',
                variant: 'contained',
                color: 'error',
                onClick: handleClearAllConfirm,
                'data-testid': 'bloomreach-templates-form-clear-all-confirm',
              },
            ],
          }}
          config={{
            open: showClearConfirm,
            onClose: () => setShowClearConfirm(false),
            showCloseIcon: true,
            closeIconTestId: 'bloomreach-templates-form-clear-all-close',
            'data-testid': 'bloomreach-templates-form-clear-all-modal',
          }}
        />
      )}
    </div>
  );
};

export default TemplateForm;
